import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { ShahadaLanguage } from '@/services/shahadaApiService'

interface LanguageOption {
  code: string
  name: string
  direction: 'ltr' | 'rtl'
}

interface ShahadaLanguageSelectorProps {
  languages: LanguageOption[]
  selectedLanguage: ShahadaLanguage
  onLanguageChange: (languageCode: string) => void
}

export default function ShahadaLanguageSelector({
  languages,
  selectedLanguage,
  onLanguageChange,
}: ShahadaLanguageSelectorProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  // Explicitly use direction from API, not browser settings
  const isRTL = selectedLanguage.direction === 'rtl'

  if (languages.length <= 1) {
    return null // Don't show selector if only one language
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="bg-[#E8F5E9] dark:bg-primary-900 text-[#2E7D32] dark:text-primary-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#C8E6C9] dark:hover:bg-primary-800 transition-colors flex items-center gap-2 border border-[#A5D6A7] dark:border-primary-700"
        aria-label="Select language"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {isRTL && (
          <ChevronDown 
            className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
          />
        )}
        <span>{selectedLanguage.languageName}</span>
        {!isRTL && (
          <ChevronDown 
            className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
          />
        )}
      </button>

      {isDropdownOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div 
            className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto z-20 w-56`}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  onLanguageChange(lang.code)
                  setIsDropdownOpen(false)
                }}
                className={`w-full text-left px-4 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/50 transition-colors ${
                  selectedLanguage.languageCode === lang.code 
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
                dir={lang.direction}
              >
                <div className="font-medium">{lang.name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{lang.code.toUpperCase()}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
